import React, { useEffect, useState } from "react";
import { getVehicles } from "../../api/apiGetrequest";
import TableHeader from "../../Components/DataTable/TableHeader";
import UserForm from "./AddVehcile";

const VehicleTable = () => {
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // New state for search input

  // Styles object
  const styles = {
    container: {
      padding: "24px",
      backgroundColor: "#f9fafb",
      minHeight: "100vh",
    },
    tabContainer: {
      display: "flex",
      gap: "8px",
      marginBottom: "24px",
      overflowX: "auto",
      padding: "4px",
    },
    tab: {
      padding: "8px 16px",
      borderRadius: "6px",
      cursor: "pointer",
      border: "none",
      transition: "all 0.2s",
      fontWeight: "500",
    },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      backgroundColor: "white",
      borderRadius: "8px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
    th: {
      padding: "12px 16px",
      textAlign: "left",
      backgroundColor: "#f8fafc",
      color: "#475569",
      fontWeight: "600",
      borderBottom: "1px solid #e2e8f0",
    },
    td: {
      padding: "12px 16px",
      borderBottom: "1px solid #e2e8f0",
      color: "#334155",
    },
    card: {
      backgroundColor: "white",
      borderRadius: "8px",
      padding: "16px",
      marginBottom: "16px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
    cardTitle: {
      fontSize: "18px",
      fontWeight: "600",
      marginBottom: "12px",
      color: "#111827",
    },
    cardItem: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "8px",
      color: "#475569",
    },
    button: {
      padding: "8px 16px",
      borderRadius: "6px",
      border: "none",
      cursor: "pointer",
      fontWeight: "500",
      transition: "all 0.2s",
    },
    editButton: {
      backgroundColor: "#3b82f6",
      color: "white",
      marginRight: "8px",
      "&:hover": {
        backgroundColor: "#2563eb",
      },
    },
    deleteButton: {
      backgroundColor: "#ef4444",
      color: "white",
      "&:hover": {
        backgroundColor: "#dc2626",
      },
    },
    status: {
      padding: "6px 12px",
      borderRadius: "999px",
      fontWeight: "500",
      textAlign: "center",
      width: "fit-content",
    },
    searchContainer: {
      marginBottom: "24px",
    },
    searchInput: {
      width: "100%",
      maxWidth: "400px",
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "16px",
      outline: "none",
      "&:focus": {
        borderColor: "#3b82f6",
        boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.2)",
      },
    },
  };

  // Status styles
  const getStatusStyle = (status) => {
    const baseStyle = { ...styles.status };
    switch (status) {
      case "Inspection Expired":
        return { ...baseStyle, backgroundColor: "#fee2e2", color: "#dc2626" };
      case "Inspection Due":
        return { ...baseStyle, backgroundColor: "#fef3c7", color: "#d97706" };
      case "Available":
        return { ...baseStyle, backgroundColor: "#dcfce7", color: "#16a34a" };
      case "WorkShop":
        return { ...baseStyle, backgroundColor: "#e0e7ff", color: "#4f46e5" };
      default:
        return { ...baseStyle, backgroundColor: "#f3f4f6", color: "#6b7280" };
    }
  };

  const fetchData = async () => {
    try {
      const result = await getVehicles();
      setVehicles(result);
      setFilteredVehicles(result);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  };

  useEffect(() => {
    fetchData();

    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Effect to handle search and tab filtering
  useEffect(() => {
    let updatedVehicles = vehicles;

    // Apply status filter
    if (activeTab !== "All") {
      updatedVehicles = vehicles?.filter(
        (vehicle) => vehicle.VehicleStatus === activeTab
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      updatedVehicles = updatedVehicles.filter((vehicle) =>
        [vehicle.VregNO, vehicle.Model, vehicle.Make]
          .filter(Boolean) // Remove null/undefined
          .some((field) =>
            field.toLowerCase().includes(searchQuery.toLowerCase())
          )
      );
    }

    setFilteredVehicles(updatedVehicles);
  }, [vehicles, activeTab, searchQuery]);

  const statuses = [
    "All",
    "Inspection Expired",
    "Inspection Due",
    "Available",
    "WorkShop",
  ];

  const handleTabClick = (status) => {
    setActiveTab(status);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleEdit = (vehicle) => {
    setEditRow(vehicle);
    setShowForm(true);
  };

  const handleDelete = (vehicle) => {
    if (window.confirm("Are you sure you want to delete this vehicle?")) {
      console.log("Delete vehicle:", vehicle);
      // Add your delete API call here
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditRow(null);
  };

  const handleFormSubmit = async (data) => {
    try {
      const result = await getVehicles(); // Refresh the list after submit
      setVehicles(result);
      setFilteredVehicles(result);
      setShowForm(false);
      setEditRow(null);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const ActionButtons = ({ vehicle }) => (
    <div>
      <button
        onClick={() => handleEdit(vehicle)}
        style={{ ...styles.button, ...styles.editButton }}
      >
        Edit
      </button>
      <button
        onClick={() => handleDelete(vehicle)}
        style={{ ...styles.button, ...styles.deleteButton }}
      >
        Delete
      </button>
    </div>
  );

  if (showForm) {
    return (
      <>
        <TableHeader
          title="Vehicle List"
          showAddButton={false}
          showBackButton={true}
          onBack={() => setShowForm(false)}
          showPrintButton={false}
          showExcelButton={false}
          showPdfButton={false}
        />

        <UserForm
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          editData={editRow}
          fetchData={fetchData}
        />
      </>
    );
  }

  return (
    <div style={styles.container}>
      <TableHeader
        title="Vehicle List"
        showAddButton={true}
        showBackButton={false}
        onAdd={() => {
          setEditRow(null);
          setShowForm(true);
        }}
        showPrintButton={true}
        showExcelButton={true}
        showPdfButton={true}
      />

      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search by Reg. Number, Model, or Make..."
          value={searchQuery}
          onChange={handleSearchChange}
          style={styles.searchInput}
        />
      </div>

      <div style={styles.tabContainer}>
        {statuses.map((status) => (
          <button
            key={status}
            onClick={() => handleTabClick(status)}
            style={{
              ...styles.tab,
              backgroundColor: activeTab === status ? "#3b82f6" : "#f3f4f6",
              color: activeTab === status ? "white" : "#6b7280",
            }}
          >
            {status}
          </button>
        ))}
      </div>

      {isSmallScreen ? (
        <div>
          {filteredVehicles.map((vehicle, index) => (
            <div key={index} style={styles.card}>
              <div style={styles.cardTitle}>{vehicle.VregNO}</div>
              <div style={styles.cardItem}>
                <span>Date Added:</span>
                <span>{new Date(vehicle.DateAdded).toLocaleString()}</span>
              </div>
              <div style={styles.cardItem}>
                <span>Model:</span>
                <span>{vehicle.Model}</span>
              </div>
              <div style={styles.cardItem}>
                <span>Make:</span>
                <span>{vehicle.Make}</span>
              </div>
              <div style={styles.cardItem}>
                <span>Inspection Date:</span>
                <span>
                  {vehicle.InspectionDate
                    ? new Date(vehicle.InspectionDate).toLocaleDateString()
                    : "N/A"}
                </span>
              </div>
              <div style={styles.cardItem}>
                <span>Status:</span>
                <span style={getStatusStyle(vehicle.VehicleStatus)}>
                  {vehicle.VehicleStatus}
                </span>
              </div>
              <ActionButtons vehicle={vehicle} />
            </div>
          ))}
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Registration Number</th>
              <th style={styles.th}>Date Added</th>
              <th style={styles.th}>Model</th>
              <th style={styles.th}>Make</th>
              <th style={styles.th}>Inspection Date</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredVehicles?.map((vehicle, index) => (
              <tr key={index}>
                <td style={styles.td}>{vehicle.VregNO}</td>
                <td style={styles.td}>
                  {new Date(vehicle.DateAdded).toLocaleString()}
                </td>
                <td style={styles.td}>{vehicle.Model}</td>
                <td style={styles.td}>{vehicle.Make}</td>
                <td style={styles.td}>
                  {vehicle.InspectionDate
                    ? new Date(vehicle.InspectionDate).toLocaleDateString()
                    : "N/A"}
                </td>
                <td style={styles.td}>
                  <div style={getStatusStyle(vehicle.VehicleStatus)}>
                    {vehicle.VehicleStatus}
                  </div>
                </td>
                <td style={styles.td}>
                  <ActionButtons vehicle={vehicle} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default VehicleTable;